<script lang="ts">
    // Utils
    import clsx from 'clsx';
    import { afterUpdate } from 'svelte';
    import { AsYouType, getCountries, getPhoneCode, parsePhoneNumber } from 'libphonenumber-js';
    import clickOutside from 'svelte-outside-click';
    import {
        getFlagEmoji,
        parseCountryCodesInPartialNumber,
        normalisePhoneCodePrefix,
    } from '$lib/components/_DisplayComponents/FormQuestion/helper';
    import uniqueId from 'lodash/uniqueId';
    import DeviceDetector from 'svelte-device-detector/dist';
    import { getBorderRadius } from '$lib/global/helper/border-radius';

    // UI
    import StatusIcon from './StatusIcon.svelte';
    import Dropdown from '$lib/components/_DisplayComponents/_wrappers/Dropdown.svelte';
    import DropdownInputPhoneCodeButtons from '$lib/components/_DisplayComponents/FormQuestion/InputTypes/DropdownInput/DropdownInputPhoneCodeButtons.svelte';
    import BottomSheet from '$lib/components/_DisplayComponents/_wrappers/BottomSheet.svelte';

    // Types
    import type { SyntheticEvent } from 'react';
    import type { CountryCode } from 'libphonenumber-js';
    import type { CustomOptions } from '$lib/types/Components/FormQuestion/CustomOptions';

    export let placeholder = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let active = false;
    export let isFirst = false;
    export let isLast = false;
    export let finalFormInput: {
        value: string;
        onChange: (value: string) => void;
        onBlur: (event: SyntheticEvent) => void;
    };
    export let errorMessage = '';
    export let customOptions: Partial<CustomOptions> = {};

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let showCheckmark = true;
    let wrapperClasses: string;
    let inputContainerClasses: string;
    let inputClasses: string;
    let countrySelectorContainerClasses: string;
    let dropdownLanguage: string;
    let selectedCountry: CountryCode = customOptions?.defaultCountry ?? 'DE';
    let countryDropdownOpen = false;
    let phoneCode = '';
    let localValue = '';
    let valueChanged = false;

    const dropdownId = uniqueId('dropdown');

    $: dropdownLanguage = customOptions?.language === 'en' ? 'GB' : 'DE';
    $: {
        try {
            const parsed = parsePhoneNumber(localValue, selectedCountry);
            finalFormInput.onChange(parsed.formatInternational());
        } catch (err) {
            finalFormInput.onChange(localValue);
        }
    }
    $: {
        phoneCode = getPhoneCode(selectedCountry);

        if (!valueChanged && phoneCode) {
            localValue = `+${phoneCode}`;
        }
    }

    afterUpdate(() => {
        showError = invalid && touched;
        showSuccess = !invalid && touched && valueChanged;
    });

    const closeDropdown = () => {
        countryDropdownOpen = false;
    };

    const openDropdown = () => {
        countryDropdownOpen = !countryDropdownOpen;
    };

    const handleClickOutside = () => {
        if (!countryDropdownOpen) return;

        closeDropdown();
    };

    const handleSelectCountry = (event: MouseEvent, code: CountryCode) => {
        selectedCountry = code;

        if (localValue.startsWith('+')) {
            localValue = formatNumber(
                `+${getPhoneCode(code)}${localValue.slice(phoneCode.length + 1)}`,
            );
        } else if (localValue.startsWith('00')) {
            localValue = formatNumber(
                `00${getPhoneCode(code)}${localValue.slice(phoneCode.length + 2)}`,
            );
        } else {
            localValue = formatNumber(localValue);
        }

        if (!localValue) {
            valueChanged = false;
        }

        closeDropdown();
    };

    const handleBlur = (event: SyntheticEvent) => {
        finalFormInput.onBlur(event);
        localValue = formatNumber(localValue);
    };

    const handleInput = (event: KeyboardEvent) => {
        valueChanged = true;

        const target = event.target as HTMLInputElement;
        const currentValue = target.value;
        let parseResult;

        try {
            parseResult = parsePhoneNumber(currentValue);
        } catch (err) {
            parseResult = undefined;
        }

        const parsedCountry = parseCountryCodesInPartialNumber(currentValue);

        if (parseResult?.country && parseResult?.country !== selectedCountry) {
            selectedCountry = parseResult.country;
        } else if (
            parsedCountry?.countryCodes?.[0] &&
            parsedCountry?.countryCodes?.[0] !== selectedCountry
        ) {
            selectedCountry = parsedCountry.countryCodes[0];
        }

        const { selectionStart, selectionEnd } = target;
        const isSelectionAtEnd =
            selectionStart === selectionEnd && selectionStart === currentValue.length;
        const formattedNumber = formatNumber(currentValue);

        localValue = isSelectionAtEnd ? formattedNumber : currentValue;
    };

    const formatNumber = (number: string) => {
        const asYouType = new AsYouType(selectedCountry);
        const { normalised, wasChanged } = normalisePhoneCodePrefix(number);
        const formattedResult = asYouType.input(normalised);

        if (!asYouType.getNumber()) {
            return number;
        }

        return wasChanged ? `00${formattedResult.slice(1)}` : formattedResult;
    };

    const borderRadius = getBorderRadius('fieldsWrapper');

    $: wrapperClasses = clsx(borderRadius, 'wrapper flex items-center relative box-border', {
        ['rounded-b-none']: !isLast,
        ['rounded-t-none']: !isFirst,
        ['border-b']: isFirst && isLast,
        invalid: invalid,
    });

    $: inputContainerClasses = clsx(
        borderRadius,
        'input-container box-border pl-[70px] w-full px-6 border-0 border-l border-r border-t mb-0 bg-white align-middle transition-all pr-0',
        {
            ['border-black/10']: !invalid,
            ['rounded-b-none']: !isLast,
            ['rounded-t-none']: !isFirst,
            ['border-b']: ((invalid && isFirst) || !isFirst) && isLast,
            ['h-[90px]']: invalid && showError,
            ['h-[64px]']: !invalid || !showError,
            ['focused bg-gray-50']: active,
        },
    );

    $: inputClasses = clsx(
        'input w-full placeholder-gray-400 font-ui text-base align-middle transition appearance-none font-light bg-transparent border-none outline-none rounded-none',
        {
            'h-full': !invalid || !showError,
            'h-[64px]': invalid && showError,
            'pr-24': !required && pristine,
            'pr-12': invalid && showError,
            'pr-10': (!invalid || !showError) && (required || !pristine),
        },
    );

    $: countrySelectorContainerClasses = clsx('absolute w-full transition-all', {
        'top-[56px]': !invalid || !showError,
        'top-[82px]': invalid && showError,
    });
</script>

<div class={wrapperClasses}>
    <DeviceDetector showInDevice={['mobile', 'tablet']}>
        <div use:clickOutside={handleClickOutside}>
            <BottomSheet open={countryDropdownOpen} handleClose={closeDropdown}>
                <DropdownInputPhoneCodeButtons
                    choices={getCountries()}
                    selected={selectedCountry}
                    value={selectedCountry}
                    open={countryDropdownOpen}
                    handleSelect={handleSelectCountry}
                    language={customOptions?.language}
                />
            </BottomSheet>
        </div>
    </DeviceDetector>

    <DeviceDetector showInDevice="desktop">
        <div class={countrySelectorContainerClasses} use:clickOutside={handleClickOutside}>
            <Dropdown
                id={dropdownId}
                open={countryDropdownOpen}
                contentStyle="w-full border-r-0 rounded-t-none border-t-0 box-border"
            >
                <DropdownInputPhoneCodeButtons
                    choices={getCountries()}
                    selected={selectedCountry}
                    value={selectedCountry}
                    open={countryDropdownOpen}
                    handleSelect={handleSelectCountry}
                    language={customOptions?.language}
                    autoFocusSearchField={true}
                />
            </Dropdown>
        </div>
    </DeviceDetector>

    <button
        on:click|preventDefault|stopPropagation={openDropdown}
        class="absolute left-6 top-0 flex h-[64px] select-none items-center gap-0.5"
    >
        <span class="text-2xl">{getFlagEmoji(selectedCountry)}</span>
        {#if countryDropdownOpen}
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
            >
                <path
                    d="M4.23967 6.20041C4.5432 5.91856 5.01775 5.93613 5.2996 6.23967L8 9.14779L10.7004 6.23967C10.9823 5.93613 11.4568 5.91856 11.7603 6.20041C12.0639 6.48226 12.0815 6.95681 11.7996 7.26034L8.5496 10.7603C8.40769 10.9132 8.20855 11 8 11C7.79145 11 7.59232 10.9132 7.45041 10.7603L4.20041 7.26034C3.91856 6.95681 3.93613 6.48226 4.23967 6.20041Z"
                    fill="#6B7280"
                />
            </svg>
        {:else}
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
            >
                <path
                    d="M4.23967 6.20041C4.5432 5.91856 5.01775 5.93613 5.2996 6.23967L8 9.14779L10.7004 6.23967C10.9823 5.93613 11.4568 5.91856 11.7603 6.20041C12.0639 6.48226 12.0815 6.95681 11.7996 7.26034L8.5496 10.7603C8.40769 10.9132 8.20855 11 8 11C7.79145 11 7.59232 10.9132 7.45041 10.7603L4.20041 7.26034C3.91856 6.95681 3.93613 6.48226 4.23967 6.20041Z"
                    fill="#6B7280"
                />
            </svg>
        {/if}
    </button>

    {#if !required && !invalid && !valueChanged && !showSuccess}
        <u class="absolute right-6 text-xs font-light text-gray-500 no-underline">(optional)</u>
    {/if}

    <StatusIcon {showCheckmark} {showError} {showSuccess} />

    <div class={inputContainerClasses}>
        <input
            class={inputClasses}
            type="tel"
            {placeholder}
            name={finalFormInput.name}
            value={localValue}
            on:blur={handleBlur}
            on:focus={finalFormInput.onFocus}
            on:input={handleInput}
        />

        {#if errorMessage && showError}
            <div class="-mt-2.5 text-xs font-normal leading-tight text-[#ef444a]">
                {errorMessage}
            </div>
        {/if}
    </div>
</div>

<style lang="scss">
    .wrapper {
        box-shadow: 0 1px 1px 0 rgba(#000, 0.1);

        :global([placeholder]) {
            @apply truncate;
        }

        .input-container.focused {
            box-shadow: 0 1px 1px 0 rgba(#000, 0.1);
        }
    }
</style>
